import { useEffect, type FC, type HTMLAttributes } from 'react'
import styles from './snackbar.module.css'

/**
 * Snackbar -- simple notification component.
 *
 * Note: Base UI provides a Toast component for more advanced use cases.
 * This Snackbar maintains MUI API compatibility for easy migration.
 * Consider migrating to Base UI Toast directly for new code.
 */
export interface SnackbarProps extends HTMLAttributes<HTMLDivElement> {
  open: boolean
  onClose?: () => void
  autoHideDuration?: number | null
  anchorOrigin?: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'center' | 'right' }
  message?: React.ReactNode
  action?: React.ReactNode
}

const verticalMap: Record<string, string | undefined> = {
  top: styles.top,
  bottom: styles.bottom,
}

const horizontalMap: Record<string, string | undefined> = {
  left: styles.left,
  center: styles.center,
  right: styles.right,
}

export const Snackbar: FC<SnackbarProps> = ({
  open,
  onClose,
  autoHideDuration,
  anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
  message,
  action,
  className,
  children,
  ...rest
}) => {
  useEffect(() => {
    if (!open || autoHideDuration == null) {return}
    const timer = setTimeout(() => onClose?.(), autoHideDuration)
    return () => clearTimeout(timer)
  }, [open, autoHideDuration, onClose])

  if (!open) {return null}

  const classes = [
    styles.root,
    verticalMap[anchorOrigin.vertical],
    horizontalMap[anchorOrigin.horizontal],
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={classes} role="status" aria-live="polite" {...rest}>
      {children ?? (
        <div className={styles.content}>
          <span style={{ flex: 1 }}>{message}</span>
          {action}
        </div>
      )}
    </div>
  )
}
